import * as THREE from 'three';
import { islandFloor, type IslandCell } from '../../shared/islandField';

/**
 * CPU-built island ground for the client streamer.
 *
 * One island becomes a pair of heightfield meshes (near + far LOD) plus a thin
 * foam ring at the waterline. The heightfield is sampled straight from the
 * shared `islandFloor` so the land the client renders is the same land the
 * server grounds the hull against. Height/slope bands are baked into vertex
 * colours (wet sand → sand → grass → rock) so the ground needs no textures and
 * only one `MeshStandardMaterial` for every island.
 *
 * The meshes live in island-local space (origin = island centre) so the owning
 * group can be positioned at the cell centre and `THREE.LOD` distances measure
 * from the island, not the world origin. Geometries are owned by the caller;
 * the materials are shared (passed in by `WorldStreamer`, which disposes them).
 */

export interface IslandTerrainOptions {
  /** Mobile profile lowers segment counts so the mobile triangle budget holds. */
  mobile: boolean;
  /** Shared vertex-colour material for every island ground mesh. */
  material: THREE.Material;
  /** Shared material for the waterline foam ring. */
  foamMaterial: THREE.Material;
}

export interface IslandTerrainBuild {
  /** Two-level ground LOD (near + far heightfields). */
  lod: THREE.LOD;
  /** Flat ring hugging the nominal shoreline, always visible. */
  foam: THREE.Mesh;
}

/**
 * The shoal ring factor mirrors `SHOAL_FACTOR` in `shared/islandField.ts`
 * (kept private there); the terrain mesh extends a little past it so its outer
 * edge can turn down into a skirt below the water surface and never z-fights
 * the ocean plane at y ≈ 0.
 */
const SHOAL_FACTOR = 1.4;
const SKIRT_END = 1.6;
/** Below-water skirt depth (metres). */
const SKIRT_Y = -4;
/** Slope (rise/run) above which a vertex reads as bare rock. */
const STEEP_SLOPE = 0.5;

/** Height/slope band palette (vertex colours). */
const WET_SAND = new THREE.Color('#6b5238');
const SAND = new THREE.Color('#d3ad6d');
const GRASS = new THREE.Color('#54853f');
const ROCK = new THREE.Color('#787c82');

const _scratch = new THREE.Color();

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function smoothstep(a: number, b: number, value: number): number {
  const t = clamp01((value - a) / (b - a));
  return t * t * (3 - 2 * t);
}

/**
 * Deterministic [-0.5, 0.5] lattice jitter for band-edge breakup. Integer
 * mixing only, no `Math.random`, so every island bands identically on the
 * client and (if ever sampled) the server.
 */
function jitter(gx: number, gz: number, seed: number): number {
  let h = (seed ^ Math.imul(gx, 0x27d4eb2d) ^ Math.imul(gz, 0x165667b1)) >>> 0;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  return h / 4294967296 - 0.5;
}

/**
 * Terrain height at a world point for one island's mesh. Land + shoal come
 * straight from the shared field; beyond the shoal ring the mesh blends down to
 * a fixed skirt so the ground never visibly meets the water surface at y = 0.
 */
function terrainHeight(x: number, z: number, cell: IslandCell): number {
  const u = Math.hypot(x - cell.x, z - cell.z) / cell.radius;
  if (u >= SKIRT_END) return SKIRT_Y;
  const floor = islandFloor(x, z);
  if (u <= SHOAL_FACTOR) return floor;
  const t = (u - SHOAL_FACTOR) / (SKIRT_END - SHOAL_FACTOR);
  return (1 - t) * 0 + t * SKIRT_Y;
}

/** Central-difference slope from the already-sampled height grid. */
function slopeFromGrid(
  heights: Float32Array,
  ix: number,
  iz: number,
  n: number,
  spacing: number,
): number {
  const left = heights[iz * n + Math.max(0, ix - 1)];
  const right = heights[iz * n + Math.min(n - 1, ix + 1)];
  const up = heights[Math.max(0, iz - 1) * n + ix];
  const down = heights[Math.min(n - 1, iz + 1) * n + ix];
  const dx = (right - left) / (2 * spacing);
  const dz = (down - up) / (2 * spacing);
  return Math.hypot(dx, dz);
}

/** Vertex colour for a normalised height + slope, with band-edge jitter. */
function terrainColor(nh: number, slope: number, jitterValue: number, out: THREE.Color): THREE.Color {
  const n = clamp01(nh + jitterValue * 0.05);

  out.copy(WET_SAND);
  if (nh < 0) {
    // Submerged shoal/skirt: darken toward a muddy seabed instead of banding.
    const deep = clamp01(-nh / 0.5);
    out.multiplyScalar(0.62 + 0.38 * (1 - deep));
  }
  out.lerp(SAND, smoothstep(0.02, 0.06, n));
  out.lerp(GRASS, smoothstep(0.12, 0.2, n));

  const rockFactor = Math.max(
    smoothstep(0.55, 0.72, n),
    smoothstep(STEEP_SLOPE * 0.5, STEEP_SLOPE, slope),
  );
  out.lerp(ROCK, rockFactor);

  // Cheap per-vertex variation so the bands don't read as flat poster fills.
  const variation = 1 + jitterValue * 0.1;
  out.r *= variation;
  out.g *= variation;
  out.b *= variation;
  return out;
}

/** Build one square heightfield grid in island-local space. */
function buildHeightfield(cell: IslandCell, segments: number, material: THREE.Material): THREE.Mesh {
  const n = segments + 1;
  const extent = cell.radius * SKIRT_END;
  const spacing = (2 * extent) / segments;
  const heights = new Float32Array(n * n);
  const positions = new Float32Array(n * n * 3);
  const colors = new Float32Array(n * n * 3);

  for (let iz = 0; iz < n; iz += 1) {
    const lz = -extent + iz * spacing;
    const wz = cell.z + lz;
    for (let ix = 0; ix < n; ix += 1) {
      const lx = -extent + ix * spacing;
      heights[iz * n + ix] = terrainHeight(cell.x + lx, wz, cell);
    }
  }

  const invHeight = 1 / cell.height;
  for (let iz = 0; iz < n; iz += 1) {
    for (let ix = 0; ix < n; ix += 1) {
      const index = iz * n + ix;
      const h = heights[index];
      const nh = h * invHeight;
      const slope = slopeFromGrid(heights, ix, iz, n, spacing);
      const jit = jitter(ix, iz, cell.seed);
      terrainColor(nh, slope, jit, _scratch);

      const offset = index * 3;
      positions[offset] = -extent + ix * spacing;
      positions[offset + 1] = h;
      positions[offset + 2] = -extent + iz * spacing;
      colors[offset] = _scratch.r;
      colors[offset + 1] = _scratch.g;
      colors[offset + 2] = _scratch.b;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setIndex(buildGridIndices(segments));
  geometry.computeVertexNormals();

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = `island-ground-${segments}`;
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Index buffer for a `segments x segments` grid. Winding is chosen so face
 * normals point +Y (computed by `computeVertexNormals` for smooth lighting).
 */
function buildGridIndices(segments: number): number[] {
  const n = segments + 1;
  const indices: number[] = [];
  for (let iz = 0; iz < segments; iz += 1) {
    for (let ix = 0; ix < segments; ix += 1) {
      const a = iz * n + ix;
      const b = a + 1;
      const c = a + n;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  return indices;
}

/** Flat ring hugging the nominal shoreline, just above the mean waterline. */
function buildFoamRing(cell: IslandCell, material: THREE.Material): THREE.Mesh {
  const inner = Math.max(1, cell.radius - 2.4);
  const outer = cell.radius + 2.4;
  const geometry = new THREE.RingGeometry(inner, outer, 96, 1);
  // `RingGeometry` is XY; rotate it flat into the XZ ground plane.
  geometry.rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'island-foam';
  mesh.position.y = 0.14;
  mesh.renderOrder = 2;
  return mesh;
}

/** Build the island ground (LOD) + foam ring in island-local space. */
export function buildIslandTerrain(cell: IslandCell, options: IslandTerrainOptions): IslandTerrainBuild {
  const nearSegments = options.mobile ? 48 : 64;
  const farSegments = options.mobile ? 20 : 24;
  const farDistance = options.mobile ? 170 : 300;

  const lod = new THREE.LOD();
  lod.name = `island-ground-lod-${cell.id}`;
  lod.addLevel(buildHeightfield(cell, nearSegments, options.material), 0);
  lod.addLevel(buildHeightfield(cell, farSegments, options.material), farDistance);
  lod.autoUpdate = true;

  return { lod, foam: buildFoamRing(cell, options.foamMaterial) };
}
